/**
 * One-shot "why you were signed out" notice for the sign-in screen (P2 —
 * INV-104). Same mechanism as `offlineAuthGrace.setReauthHint` (sessionStorage,
 * per-tab, consumed once by `pages/Login.tsx`), separate module because this is
 * an AUTHORIZATION refusal, not an offline-grace event.
 *
 * P2's `jwt.strategy` resolves role/clinicId/permissions from Postgres on every
 * request and fails CLOSED with two coded 401s that are PERMANENT for this
 * identity: `USER_INACTIVE` (the User row is deactivated) and
 * `USER_NOT_PROVISIONED` (no User row for this Firebase uid). Neither can be
 * cleared by anything the client controls — re-authenticating at Firebase
 * succeeds and the very next request 401s again — so AuthProvider signs the user
 * out instead of retrying or raising the SESSION_EXPIRED re-auth dialog, and
 * leaves the reason here. Without it the user lands on a bare login screen,
 * signs in successfully, and is bounced again with no explanation.
 */

import { OFFLINE_ENABLED } from '../offline/entities';

/** `UNLOCK_ATTEMPTS_EXHAUSTED`: a workstation lock ran out of PIN tries (workstation-lock plan T5). */
export type SignOutNoticeCode = 'USER_INACTIVE' | 'USER_NOT_PROVISIONED' | 'UNLOCK_ATTEMPTS_EXHAUSTED';

/**
 * A deactivated vet can have unsynced clinical work on this device and can never
 * sign in again to push it. `wipeReplicaOnSessionEnd` PRESERVES that queue (B3) and
 * nothing told them so — they are meant to hand the device to someone who can sync
 * it, not assume the work is gone. Only ever said where a replica exists: with the
 * offline flag off, nothing was stored and the sentence would be a lie.
 */
const UNSYNCED_WORK_KEPT = ' Your unsynced changes are saved on this device.';

/** The one place these sentences live; Login renders them verbatim. */
export const SIGN_OUT_NOTICE_MESSAGES: Record<SignOutNoticeCode, string> = {
  USER_INACTIVE:
    'Your account has been deactivated. Contact your clinic administrator.' +
    (OFFLINE_ENABLED ? UNSYNCED_WORK_KEPT : ''),
  // No sentence here: this identity never had a session, so there is no local work.
  USER_NOT_PROVISIONED:
    'Your account is not set up for this clinic yet. Contact your clinic administrator.',
  UNLOCK_ATTEMPTS_EXHAUSTED: 'Too many wrong PINs. Sign in with your password.',
};

const NOTICE_KEY = 'st.auth.signOutNotice';

export function setSignOutNotice(code: SignOutNoticeCode): void {
  try {
    sessionStorage.setItem(NOTICE_KEY, code);
  } catch {
    /* storage unavailable — the sign-out still happens, just unexplained */
  }
}

/** The pending notice, or null. Clears it (one-shot); an unrecognised stored
 *  value reads as null so a stale/garbage key can never render as a message.
 *
 *  ⚠ `hasOwnProperty`, not `in`: `in` walks the PROTOTYPE chain, so a stored
 *  'toString' / 'constructor' passed the guard and `Login` then rendered a
 *  FUNCTION as a React child. Same reason `jwt.strategy.ts` uses
 *  `Object.keys().includes()` over `in`. */
export function consumeSignOutNotice(): SignOutNoticeCode | null {
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY);
    sessionStorage.removeItem(NOTICE_KEY);
    return raw != null && Object.prototype.hasOwnProperty.call(SIGN_OUT_NOTICE_MESSAGES, raw)
      ? (raw as SignOutNoticeCode)
      : null;
  } catch {
    return null;
  }
}
